import React, { useState, useEffect, useMemo, useContext } from "react";
import SelectOption from "components/common/Select/Select";
import { useNavigate } from "react-router";
import * as FileSaver from "file-saver";
import { toast } from "react-toastify";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import SideBar from "components/common/SideBar/SideBarNew";
import TableHeader from "components/common/Table/TableHeader";
import CreatedCosInvoiceTabel from "./COSInvoiceTabel";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import SelectBox from "components/common/DropDownWithIocns/DropDown";
import CheckBox from "components/common/checkBox/checkbox";
import {
  COS_BREADCRUMB,
  COS_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import { LOADER_STATUS, USER_ROLE } from "Constants/constant";
import { LoginContext } from "helpers/LoginContext";
import { roleAccess } from "helpers/RolesPermission";
import { BiPlus } from "react-icons/bi";
import swal from "sweetalert";
import api from "store/services/cos.services";
import useCareLabels from "hooks/useCareLabels";

const COSInvoiceListPage = (props) => {
  const {
    createdCosInvoiceLoading,
    getInvoicesList,
    createdCosInvoice,
    cosInvoiceLoading,
    emptyTableData,
    cosInvoiceMode,
    getCosInvoiceMode,
    updateCosInvoiceMode,
    changeInvoiceStatus,
    changeInvoiceStatusLoading,
  } = props;

  const navigate = useNavigate();
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const { participantLabel } = useCareLabels();
  /** Black portal (worker) logins must not see Xero export / connect in COS invoices. */
  const isWorkerBlackPortal = roleAccess([USER_ROLE.Sp_Standard_User]);
  const [renderLoading, setRenderLoading] = useState(LOADER_STATUS.NA);
  const [selectedMode, setSelectedMode] = useState(cosInvoiceMode || "old");
  const [checkboxes, setCheckboxes] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [updatedAction, setUpdatedAction] = useState(null);
  const [fileDownloaderLoader, setFileDownloaderLoader] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const createdCosInvoiceToMap = useMemo(() => {
    if (createdCosInvoice && createdCosInvoice.length > 0) {
      if (selectedParticipant) {
        return createdCosInvoice.filter(
          (inv) => inv.participant_name === selectedParticipant.value
        );
      }
      return createdCosInvoice;
    }
    return [];
  }, [createdCosInvoice, selectedParticipant]);

  const participantOptions = useMemo(() => {
    if (createdCosInvoice && createdCosInvoice.length > 0) {
      const unique = [
        ...new Set(
          createdCosInvoice
            .map((inv) => inv.participant_name)
            .filter(Boolean)
        ),
      ];
      return unique.map((name) => ({ label: name, value: name }));
    }
    return [];
  }, [createdCosInvoice]);

  useEffect(() => {
    getInvoicesList();
    getCosInvoiceMode();
    return () => emptyTableData();
  }, []);

  useEffect(() => {
    if (cosInvoiceMode) {
      setSelectedMode(cosInvoiceMode);
    }
  }, [cosInvoiceMode]);

  useEffect(() => {
    if (!cosInvoiceLoading && renderLoading !== LOADER_STATUS.NA) {
      setRenderLoading(LOADER_STATUS.COMPLATE);
    }
  }, [cosInvoiceLoading]);

  // Checkbox management
  const handleCheckboxChange = (e, uuid) => {
    const filteredList = checkboxes.filter((c) => c !== uuid);
    if (e) {
      filteredList.push(uuid);
    }
    setCheckboxes(filteredList);
  };

  const toggleCheckboxes = (val) => {
    setCheckboxes(
      val ? createdCosInvoiceToMap.map((item) => item.uuid) : []
    );
  };

  useEffect(() => {
    if (
      createdCosInvoiceToMap &&
      checkboxes &&
      checkboxes.length === createdCosInvoiceToMap.length &&
      createdCosInvoiceToMap.length !== 0
    ) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [checkboxes, createdCosInvoiceToMap]);

  const getSelectedCaseNoteIds = () => {
    const selectedInvoices = createdCosInvoiceToMap.filter((inv) =>
      checkboxes.includes(inv.uuid)
    );
    const allIds = [];
    selectedInvoices.forEach((inv) => {
      if (inv.payload && Array.isArray(inv.payload)) {
        allIds.push(...inv.payload);
      } else if (inv.cos_case_notes_for_invoice && Array.isArray(inv.cos_case_notes_for_invoice)) {
        allIds.push(...inv.cos_case_notes_for_invoice.map((cn) => cn.id));
      }
    });
    return allIds;
  };

  // Batch action handling (participant / new mode only; batch users act on invoice preview)
  useEffect(() => {
    if (selectedMode !== "new") return;
    if (updatedAction && checkboxes.length > 0) {
      handleChangeAction();
    }
  }, [updatedAction, selectedMode, checkboxes.length]);

  const getText = () => {
    if (updatedAction === "revert") {
      return "Are you sure you want to Revert all case notes in the selected invoice(s)?";
    } else if (updatedAction === "cancelled") {
      return "Are you sure you want to Cancel all case notes in the selected invoice(s)?";
    } else if (updatedAction === "extract_generated") {
      return "Are you sure you want to Undo Cancel for the selected invoice(s)?";
    } else if (updatedAction === "provider_paid") {
      return "Are you sure you want to mark the selected invoice(s) as Provider Paid?";
    } else if (updatedAction === "xero") {
      return "Are you sure you want to Export the selected invoice(s) to Xero?";
    } else if (updatedAction === "email_invoice") {
      return "Are you sure you want to email the selected invoice(s) to the participant's invoicing email recipients?";
    } else {
      return "Are you sure you want to proceed?";
    }
  };

  const handleChangeAction = async () => {
    if (isUserGracePeriodOver) return;
    if (selectedMode !== "new") {
      setUpdatedAction("");
      return;
    }

    const caseNoteIds = getSelectedCaseNoteIds();
    if (caseNoteIds.length === 0) {
      toast.warn("No case notes found in the selected invoices.");
      setUpdatedAction("");
      return;
    }

    // File exports (PDF / XLSX)
    if (
      updatedAction &&
      checkboxes.length > 0 &&
      (updatedAction === "pdf" || updatedAction === "xlsx")
    ) {
      try {
        setFileDownloaderLoader(true);
        const res = await api.changeActionsClaimInvoiceApi({
          report: updatedAction,
          cos_invoice_ids: caseNoteIds,
        });

        if (res && res.report) {
          await FileSaver.saveAs(
            res.report.download_url,
            `Invoices-${updatedAction === "xlsx" ? "EXCEL" : "PDF"}-File${updatedAction === "xlsx" ? ".csv" : ".pdf"
            }`
          );
        }
        setCheckboxes([]);
        setUpdatedAction("");
        setFileDownloaderLoader(false);
      } catch (err) {
        setUpdatedAction("");
        setFileDownloaderLoader(false);
        console.log("err", err);
        toast.error("Something went wrong while downloading!");
      }
      return;
    }

    // Status change actions
    swal({
      title: "Confirm your action?",
      text: getText(),
      icon: "",
      buttons: ["No, take me back!", "Yes, go ahead!"],
      dangerMode: updatedAction === "cancelled" || updatedAction === "revert",
    }).then((willDelete) => {
      if (isUserGracePeriodOver) return;
      if (willDelete) {
        if (caseNoteIds.length > 0) {
          changeInvoiceStatus({
            status: updatedAction,
            cos_invoice_ids: caseNoteIds,
          });
          setCheckboxes([]);
          setUpdatedAction("");
        }
      } else {
        setCheckboxes([]);
        setUpdatedAction("");
      }
    });
  };

  // Handle row-level download
  const handleDownload = async (item, type) => {
    if (isUserGracePeriodOver) return;

    const invoiceIds = [String(item.id)];

    try {
      setFileDownloaderLoader(true);
      const res = await api.changeActionsClaimInvoiceApi({
        report: type,
        cos_invoice_ids: invoiceIds,
        download_by_invoice: true,
      });

      if (res && res.report) {
        await FileSaver.saveAs(
          res.report.download_url,
          `Invoice-${item.ref_no}-${type === "xlsx" ? "EXCEL" : "PDF"}-File${type === "xlsx" ? ".csv" : ".pdf"
          }`
        );
      }
      setFileDownloaderLoader(false);
    } catch (err) {
      setFileDownloaderLoader(false);
      console.log("err", err);
      toast.error("Something went wrong while downloading!");
    }
  };

  // Handle per-row email invoice
  const handleEmailInvoice = (item) => {
    if (isUserGracePeriodOver) return;
    if (item?.email_sent) {
      toast.info("Invoice has already been emailed");
      return;
    }
    const caseNoteIds = item.cos_case_notes_for_invoice?.map((cn) => String(cn.id)) || [];
    if (caseNoteIds.length === 0) {
      toast.warn("No case notes found for this invoice.");
      return;
    }
    swal({
      title: "Email Invoice?",
      text: "Are you sure you want to email this invoice to the participant's invoicing email recipients?",
      icon: "",
      buttons: ["No, take me back!", "Yes, send email!"],
      dangerMode: false,
    }).then((confirmed) => {
      if (confirmed) {
        changeInvoiceStatus({
          status: "email_invoice",
          cos_invoice_ids: caseNoteIds,
        });
      }
    });
  };

  const handleRowAction = (action, item) => {
    let text = "Are you sure you want to perform this action?";
    switch (action) {
      case "paid":
        text = "You want to Mark as Paid !";
        break;
      case "revert":
        text = "You want to Mark as Revert !";
        break;
      case "cancelled":
        text = "You want to cancel !";
        break;
      case "undo_cancel":
        text = "You want to undo cancel !";
        break;
      case "Export to Xero":
        text = "You want to send this invoice to xero !";
        break;
      default:
        break;
    }

    swal({
      title: "Confirm your action?",
      text: text,
      icon: "",
      buttons: ["No, take me back!", "Yes, go ahead!"],
      dangerMode: action === "cancelled" || action === "revert",
    }).then((willDelete) => {
      if (isUserGracePeriodOver) return;
      if (willDelete) {
        changeInvoiceStatus({
          status: action,
          cos_invoice_ids: [String(item.id)],
        });
      }
    });
  };

  const handleModeChange = (e) => {
    const mode = e.target.value;
    setSelectedMode(mode);
    updateCosInvoiceMode({ mode });
  };

  const isNewMode = selectedMode === "new";
  const isLoading = createdCosInvoiceLoading || changeInvoiceStatusLoading || fileDownloaderLoader;

  return (
    <div className='min-h-full'>
      <SideBar />
      <div
        className={`${sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
          }  flex flex-col`}
      >
        <main className='h-screen flex-1 bg-white'>
          <Header
            title='Cos Invoices'
            breadcrumb={
              <Breadcrumb
                pages={COS_BREADCRUMB(COS_BREADCRUMB_PAGES.CREATED_COS_INVOICE)}
              />
            }
            handleClick={() => navigate("/cos/invoice-new")}
            btnLabel='Cos Invoice'
            icon={<BiPlus />}
            isButton
          />
          <div className='w-full px-4 py-3 bg-white'>
            <div className='mb-5 rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white px-4 py-4 shadow-sm sm:px-6'>
              <div
                className={
                  isNewMode
                    ? "grid grid-cols-1 items-start gap-x-4 gap-y-4 sm:grid-cols-1 md:grid-cols-12"
                    : "max-w-2xl"
                }
              >
                <div
                  className={`min-w-0 ${
                    isNewMode ? "md:col-span-4 lg:col-span-5" : ""
                  }`}
                >
                  <label
                    htmlFor='invoice-mode-selector'
                    className='mb-1.5 block text-sm font-medium text-slate-800'
                  >
                    Invoice type
                  </label>
                  <select
                    id='invoice-mode-selector'
                    value={selectedMode}
                    onChange={handleModeChange}
                    className='block h-10 w-full min-w-0 rounded-lg border-slate-300 bg-white pl-3 pr-8 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-0 sm:max-w-lg lg:min-w-[18rem]'
                    disabled={isLoading}
                  >
                    <option value='new'>{participantLabel} based</option>
                    <option value='old'>Case note based</option>
                  </select>
                </div>
                  {isNewMode && (
                    <div className='min-w-0 w-full md:col-span-4 lg:col-span-4'>
                      <label
                        className='mb-1.5 block text-sm font-medium text-slate-800'
                        htmlFor='participant-invoice-filter'
                      >
                        Show invoices for
                      </label>
                      <div
                        id='participant-invoice-filter'
                        className='[&_.select-basic-single>div]:!min-h-[2.5rem]'
                      >
                        <SelectOption
                          isClearable={true}
                          isDisabled={isLoading}
                          cssClass=''
                          name='participant-filter'
                          isSearch={true}
                          data={participantOptions}
                          selectedOption={selectedParticipant}
                          handleChange={(e) => setSelectedParticipant(e)}
                          placeHolder='All participants'
                        />
                      </div>
                    </div>
                  )}
                  {isNewMode && (
                    <div className='w-full min-w-0 md:col-span-4 md:min-w-0 lg:col-span-3'>
                      <span className='mb-1.5 block text-sm font-medium text-slate-800'>
                        Bulk action
                      </span>
                      <div className='w-full [&>div]:!block [&>div]:w-full [&_button]:!h-10 [&_button]:!min-h-[2.5rem] [&_button]:!py-0'>
                        <SelectBox
                          disabled={
                            (checkboxes.length > 0 ? false : true) ||
                            isUserGracePeriodOver
                          }
                          setUpdatedAction={setUpdatedAction}
                          isForCosModule={false}
                          allowXeroExport={!isWorkerBlackPortal}
                          showExportReports={false}
                          allowEmailInvoice={true}
                          hideCancelDsBulkOptions
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className='sm:px-6 lg:px-4 py-4'>
              <div className='flex flex-col'>
                <div className='min-w-full'>
                  <div className='shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
                    <div className='table-wrp block'>
                      <table className='min-w-full divide-y divide-gray-300'>
                        <thead
                          className='bg-blue-50 sticky z-[5] capitalize'
                          style={{ top: "80px" }}
                        >
                          <tr>
                            {isNewMode && (
                              <th
                                scope='col'
                                className='pl-3 text-center text-sm font-semibold text-gray-900 bg-blue-50'
                              >
                                <CheckBox
                                  isDisabled={isUserGracePeriodOver}
                                  checked={allSelected}
                                  handleChange={(e) => {
                                    if (isUserGracePeriodOver) return;
                                    if (
                                      createdCosInvoiceToMap &&
                                      createdCosInvoiceToMap.length > 0
                                    ) {
                                      setAllSelected(e);
                                      toggleCheckboxes(e);
                                    }
                                  }}
                                />
                              </th>
                            )}
                            <TableHeader
                              sortName='reference'
                              isSort={false}
                              label={"Reference"}
                            />
                            {isNewMode && (
                              <TableHeader
                                sortName='participant'
                                isSort={false}
                                label={participantLabel}
                              />
                            )}
                            <TableHeader
                              sortName='created_by'
                              isSort={false}
                              label={"Created By"}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='created-at'
                              label={"Created Date"}
                            />
                            <TableHeader
                              isSort={false}
                              sortName='support_item_no'
                              label={"Service Delivered From"}
                              cssClass='text-center'
                            />
                            <TableHeader
                              isSort={false}
                              sortName='created_by'
                              label={"Service Delivered To"}
                              cssClass='text-center'
                            />
                            <TableHeader
                              isSort={false}
                              sortName='client'
                              label={"Num Delivered Services"}
                              cssClass='text-center'
                            />
                            <TableHeader
                              isSort={false}
                              sortName='management_style'
                              label={"Total Value"}
                              cssClass='text-center'
                            />
                            {isNewMode && (
                              <TableHeader
                                isSort={false}
                                sortName='invoice_status'
                                label={"Status"}
                                cssClass='text-center'
                              />
                            )}
                            <TableHeader
                              isSort={false}
                              sortName='status'
                              label={"Actions"}
                            />
                            <th>
                              <div className='sr-only'>View</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-200 bg-white'>
                          {isLoading ? (
                            <tr>
                              <td
                                className='w-full text-center py-4 bg-gray-50'
                                colSpan={isNewMode ? 12 : 8}
                              >
                                <CircularLoader
                                  classes='flex justify-center items-center'
                                  classloader='loader-l'
                                />
                              </td>
                            </tr>
                          ) : createdCosInvoiceToMap &&
                            createdCosInvoiceToMap.length > 0 ? (
                            createdCosInvoiceToMap.map((item, index) => (
                              <CreatedCosInvoiceTabel
                                item={item}
                                key={index}
                                isNewMode={isNewMode}
                                isUserGracePeriodOver={isUserGracePeriodOver}
                                checkboxes={checkboxes}
                                handleCheckboxChange={(e) => {
                                  if (isUserGracePeriodOver) return;
                                  handleCheckboxChange(e, item.uuid);
                                }}
                                handleDownload={handleDownload}
                                handleEmailInvoice={handleEmailInvoice}
                                handleRowAction={handleRowAction}
                              />
                            ))
                          ) : (
                            createdCosInvoiceToMap.length === 0 && (
                              <tr>
                                <td
                                  colSpan={isNewMode ? 12 : 8}
                                  className='py-6 text-center text-red-600'
                                >
                                  No Data Found!
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default COSInvoiceListPage;
